import {wixImageResourceGetResizedImageUrl} from "@wix/devzai-common-wix";
import {
    arrayOrderBy,
    assertDefined,
    compareOrderedValues,
    evaluateFunction,
    HtmlObjectHorizontalPosition,
    HtmlObjectResizeMode,
    HtmlObjectVerticalPosition,
    stringReplaceEmptyOrWhiteSpaceValue,
    validateIsUrl,
    VcfFieldsLabelingMethod,
    vcfGenerate,
    VcfUrlFieldTypes
} from "@wix/devzai-utils-common";
import {browserConvertImageToBase64, browserDownloadStringAsFile} from "@wix/devzai-utils-browser";
import {doppeHideableValueGetVisibleValue} from "../../client-server-common";
import {
    doppeViewerContactMethodIsEmail,
    doppeViewerContactMethodIsLocation,
    doppeViewerContactMethodIsPhoneNumber
} from "../../components/doppe-viewer-contact-methods/doppe-viewer-contact-methods";
import {
    doppeDtoContactMethodLinkResolveValue,
    DoppeDtoContactMethods
} from "../../client-server-common/types/doppe-dto-contact-method";
import {DoppeDtoContactDetails} from "../../client-server-common/types/doppe-dto-contact-details";

export async function doppeDtoContactDetailsDownloadContactCard(contactDetails: DoppeDtoContactDetails) {

    const {
        name,
        media,
        jobTitle,
        contactMethods,
        company,
        notes,
    } = contactDetails;

    const firstName = name?.firstName ?? '';
    const lastName = name?.lastName ?? '';

    const calculatedName = firstName || lastName ? `${firstName} ${lastName}` : 'Contact';
    const imageForContact = media;

    const resizedImageUrl = imageForContact ? wixImageResourceGetResizedImageUrl(imageForContact, {
        resizeMode: HtmlObjectResizeMode.Cover,
        targetHeight: 350,
        targetWidth: 350,
        cropImage: {
            positionVertical: HtmlObjectVerticalPosition.Center,
            positionHorizontal: HtmlObjectHorizontalPosition.Center
        }
    }) : null;

    const imageToDownload = resizedImageUrl ? await browserConvertImageToBase64(resizedImageUrl) : undefined;

    browserDownloadStringAsFile(vcfGenerate({
        fieldsLabelingMethod: VcfFieldsLabelingMethod.UsingCustomTypes,
        firstName: firstName,
        lastName: lastName,
        organization: company,
        jobTitle: doppeHideableValueGetVisibleValue(jobTitle, ''),
        emails: contactMethods.filter(method => {
            return doppeViewerContactMethodIsEmail(method.type);
        }).map(method => {
            return {
                email: method.displayValue,
                type: 'work'
            };
        }),
        phoneNumbers: [...contactMethods.filter(method => {
            return doppeViewerContactMethodIsPhoneNumber(method.type);
        }), ...contactMethods.filter(method => {
            return method.type === DoppeDtoContactMethods.WhatsApp;
        })].map(method => {
            return {
                phoneNumber: method.displayValue,
                type: 'work'
            };
        }),
        urls: arrayOrderBy(
            contactMethods.filter(method => {
                return (validateIsUrl(method.displayValue) && !doppeViewerContactMethodIsEmail(method.type)) || (method.type === DoppeDtoContactMethods.FacebookMessenger && doppeDtoContactMethodLinkResolveValue(method) !== undefined)
            }),
            contactMethod => contactMethod.type,
            (methodType1, methodType2) => compareOrderedValues(methodType1, methodType2, [DoppeDtoContactMethods.Website])
        ).map(method => {
            return {
                url: assertDefined(doppeDtoContactMethodLinkResolveValue(method)),
                type: evaluateFunction(() => {
                    return stringReplaceEmptyOrWhiteSpaceValue(doppeHideableValueGetVisibleValue(method.title, undefined), undefined) ?? evaluateFunction(() => {
                        switch (method.type) {
                            case DoppeDtoContactMethods.Instagram: {
                                return 'Instagram';
                            }
                            case DoppeDtoContactMethods.LinkedIn: {
                                return 'LinkedIn';
                            }
                            case DoppeDtoContactMethods.Facebook: {
                                return 'Facebook';
                            }
                            case DoppeDtoContactMethods.FacebookMessenger: {
                                return 'Facebook Messenger';
                            }
                            case DoppeDtoContactMethods.WhatsApp: {
                                return 'WhatsApp';
                            }
                            case DoppeDtoContactMethods.Website: {
                                return VcfUrlFieldTypes.Work;
                            }
                            default: {
                                return VcfUrlFieldTypes.Other;
                            }
                        }
                    })
                })
            };
        }),
        address: contactMethods.filter(method => {
            return doppeViewerContactMethodIsLocation(method.type);
        })[0]?.displayValue ?? '',
        note: notes,
        photoUrl: imageToDownload,
    }), `${calculatedName}.vcf`);
}